import { FC, useEffect, useRef, useState } from 'react';
import { ChatMessage } from '../types';
import { ProactiveInsight } from '../hooks/useProactiveInsights';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import InsightBanner from './InsightBanner';
import { SUGGESTED_QUESTIONS, WELCOME_MESSAGES } from '../constants';

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  insights: ProactiveInsight[];
  onSendMessage: (message: string) => void;
  onClearHistory: () => void;
  onDismissInsight: (id: string) => void;
  onClearError: () => void;
}

const ChatPanel: FC<ChatPanelProps> = ({
  isOpen,
  onClose,
  messages,
  isStreaming,
  error,
  insights,
  onSendMessage,
  onClearHistory,
  onDismissInsight,
  onClearError,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [welcomeMessage] = useState(() => WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        // Check if click is on the chat bubble
        const target = event.target as HTMLElement;
        if (!target.closest('[aria-label="Open chat assistant"]')) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSuggestedQuestion = (question: string) => {
    onSendMessage(question);
  };

  const displayMessages = messages.filter((m) => m.role !== 'tool');
  const isEmpty = displayMessages.length === 0;

  return (
    <div
      className={`fixed bottom-24 right-6 z-40 transition-all duration-300 ease-out ${
        isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <div
        ref={panelRef}
        className="w-[400px] max-w-[calc(100vw-48px)] h-[620px] max-h-[calc(100vh-160px)] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl dark:shadow-slate-900/50 flex flex-col overflow-hidden border border-gray-200 dark:border-slate-700"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 bg-gradient-to-r from-slate-700 via-slate-800 to-slate-700 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800 text-white">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange to-orange/80 dark:from-teal-500 dark:to-teal-600 flex items-center justify-center shadow-lg shadow-orange/30 dark:shadow-teal-500/30">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            </div>
            <span className="font-semibold text-base">Portfolio Assistant</span>
          </div>
          <div className="flex items-center gap-1">
            {!isEmpty && (
              <button
                onClick={onClearHistory}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Clear chat history"
                title="Clear history"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Close chat"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="px-4 py-2.5 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/10 border-b border-red-200 dark:border-red-700/30 flex items-center justify-between">
            <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
            <button
              onClick={onClearError}
              className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Messages area */}
        <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-slate-900">
          {isEmpty ? (
            <div className="flex-1 px-4 py-5 overflow-y-auto">
              {/* Proactive insights */}
              {insights.length > 0 && (
                <div className="mb-5">
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Insights
                  </h4>
                  {insights.slice(0, 2).map((insight) => (
                    <InsightBanner
                      key={insight.id}
                      insight={insight}
                      onDismiss={onDismissInsight}
                      onNavigate={onClose}
                    />
                  ))}
                </div>
              )}

              {/* Welcome message */}
              <div className="bg-white dark:bg-slate-800 rounded-xl px-4 py-3 mb-5 shadow-sm border border-gray-100 dark:border-slate-700">
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{welcomeMessage}</p>
              </div>

              {/* Suggested questions */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Try asking
                </h4>
                <div className="space-y-2">
                  {SUGGESTED_QUESTIONS.slice(0, 4).map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestedQuestion(question)}
                      className="w-full text-left px-4 py-3 text-sm rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:border-orange dark:hover:border-teal-500 hover:bg-orange/5 dark:hover:bg-teal-500/10 hover:shadow-sm transition-all duration-200"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Insights at top of conversation if any */}
              {insights.length > 0 && (
                <div className="px-4 pt-3 pb-2 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                  {insights.slice(0, 1).map((insight) => (
                    <InsightBanner
                      key={insight.id}
                      insight={insight}
                      onDismiss={onDismissInsight}
                      onNavigate={onClose}
                    />
                  ))}
                </div>
              )}
              <MessageList messages={messages} onNavigate={onClose} />
            </>
          )}
        </div>

        {/* Input */}
        <ChatInput onSend={onSendMessage} disabled={isStreaming} />
      </div>
    </div>
  );
};

export default ChatPanel;
